import {
  Box,
  withStyles,
  AccountLimitsResponse,
  AccountBalanceResponse,
  formattedCurrency,
  capitalizeFirstLetter,
  getCSSVariable,
} from 'vopay';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TabButton from '../../components/TabButton';
import ProgressGraph from '../../components/ProgressGraph';
import styles from './AccountLimits.module.scss';

import { Title } from 'UI';

const cn = withStyles(styles);

type AccountLimitsProps = {
  accountLimitsResponse: AccountLimitsResponse;
  accountBalanceResponse: AccountBalanceResponse;
};

type Tab = 'daily' | 'weekly' | 'monthly';
type FC = FunctionComponent<AccountLimitsProps>;

type AccountLimitProps = {
  type: 'Inbound' | 'Outbound';
  title: string;
  remainingAmount: string;
  startingAmount: string;
  remaining: string;
  total: string;
  currency: string;
  primaryColor: string;
};

const AccountLimit: FunctionComponent<AccountLimitProps> = ({
  title,
  remainingAmount,
  startingAmount,
  remaining,
  total,
  currency,
  primaryColor,
}) => {
  return (
    <div className={cn('accountLimitsColumn')}>
      <Title level="6" emphasized={false} style={{ color: 'rgba(45, 45, 45, 0.50)' }}>
        {title}
      </Title>
      <div className={cn('graph')}>
        <ProgressGraph
          remaining={+remaining}
          total={+total}
          pathStrokeColor={primaryColor}
          trailStrokeColor={`${primaryColor}1A`}
          width="180px"
          height="180px"
        />
      </div>
      <div>
        <Title level="6" emphasized={false} style={{ color: 'rgba(45, 45, 45, 0.50)', fontSize: '13px' }}>
          {remainingAmount}
        </Title>
        <span>
          <Title level="6" emphasized={false}>
            {currency}
          </Title>
          <Title level="4">{formattedCurrency(remaining)}</Title>
        </span>
      </div>
      <div>
        <Title level="6" emphasized={false} style={{ color: 'rgba(45, 45, 45, 0.50)', fontSize: '13px' }}>
          {startingAmount}
        </Title>
        <span>
          <Title level="6" emphasized={false}>
            {currency}
          </Title>
          <Title level="6" style={{ fontWeight: 600 }}>
            {formattedCurrency(total)}
          </Title>
        </span>
      </div>
    </div>
  );
};

const AccountLimits: FC = ({ accountLimitsResponse, accountBalanceResponse }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp.Widgets.AccountLimits' });
  const [activeTab, setActiveTab] = useState<Tab>('daily');
  const limitType = capitalizeFirstLetter(activeTab) as 'Daily' | 'Weekly' | 'Monthly';
  const primaryColor = getCSSVariable('--primaryColor');
  const currency = accountBalanceResponse?.Currency || '';

  const options: { label: string; value: Tab }[] = [
    { label: t('daily'), value: 'daily' },
    { label: t('weekly'), value: 'weekly' },
    { label: t('monthly'), value: 'monthly' },
  ];

  const parseAmount = (value: string | undefined | null): string => {
    const num = Number(value);
    return isNaN(num) ? '0.00' : String(num);
  };

  return (
    <>
      <Box ai="center" jc="sb" mb="md" height="auto">
        <Title level="5">{t('title')}</Title>
        <TabButton options={options} value={activeTab} onSelect={setActiveTab} />
      </Box>
      <div className={cn('accountLimitsContainer')}>
        {accountLimitsResponse.Success && (
          <>
            <AccountLimit
              type="Inbound"
              title={t('fundLimit')}
              remainingAmount={t('remainingAmount')}
              startingAmount={t('startingAmount')}
              remaining={parseAmount(accountLimitsResponse.Inbound.RemainingLimits[limitType])}
              total={parseAmount(accountLimitsResponse.Inbound.Limits[limitType])}
              currency={currency}
              primaryColor={primaryColor}
            />
            <AccountLimit
              type="Outbound"
              title={t('withdrawLimit')}
              remainingAmount={t('remainingAmount')}
              startingAmount={t('startingAmount')}
              remaining={parseAmount(accountLimitsResponse.Outbound.RemainingLimits[limitType])}
              total={parseAmount(accountLimitsResponse.Outbound.Limits[limitType])}
              currency={currency}
              primaryColor={primaryColor}
            />
          </>
        )}
      </div>
    </>
  );
};

export default AccountLimits;
